import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  api,
  Comment,
  Dashboard,
  DashboardVersion,
  ShareUser,
  SignedUrlRecord,
  SIGNED_URL_TTL_OPTIONS,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Skeleton } from '../components/Layout';

function StarRating({ dashboardId, initialAverage, initialCount }: { dashboardId: string; initialAverage?: number | null; initialCount?: number }) {
  const { user } = useAuth();
  const [myRating, setMyRating] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [aggregate, setAggregate] = useState<{ average: number | null; count: number }>(
    { average: initialAverage ?? null, count: initialCount ?? 0 }
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.getMyRating(dashboardId).then((r) => setMyRating(r.rating)).catch(() => {});
  }, [dashboardId, user]);

  const rate = async (star: number) => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const res = await api.rateCanvas(dashboardId, star);
      setMyRating(res.rating);
      setAggregate({ average: res.ratingAverage, count: res.ratingCount });
    } finally {
      setSaving(false);
    }
  };

  const display = hovered ?? myRating ?? 0;

  return (
    <section className="rating-section">
      <h3 className="rating-label">User Ratings</h3>
      <div className="rating-row">
        <div className="rating-stars" aria-label="Rate this canvas">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className={`rating-star${display >= star ? ' filled' : ''}`}
              onClick={() => rate(star)}
              onMouseEnter={() => user && setHovered(star)}
              onMouseLeave={() => setHovered(null)}
              aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
              disabled={!user || saving}
            >
              ★
            </button>
          ))}
        </div>
        {aggregate.average !== null && aggregate.count > 0 && (
          <span className="rating-aggregate">
            {aggregate.average.toFixed(1)} / 5 ({aggregate.count} {aggregate.count === 1 ? 'rating' : 'ratings'})
          </span>
        )}
        {myRating !== null && (
          <span className="rating-mine muted">Your rating: {myRating} / 5</span>
        )}
        {!user && <span className="rating-hint muted">Sign in to rate</span>}
      </div>
    </section>
  );
}

const PAGE_SIZE = 10;

function CommentsSection({ dashboardId }: { dashboardId: string }) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [nextKey, setNextKey] = useState<string | null>(null);
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const load = async (s: 'newest' | 'oldest', lastKey?: string) => {
    setLoading(true);
    try {
      const res = await api.listComments(dashboardId, { limit: PAGE_SIZE, sort: s, lastKey });
      setComments((prev) => lastKey ? [...prev, ...res.comments] : res.comments);
      setNextKey(res.nextKey);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(sort); }, [dashboardId, sort]);

  const changeSort = (s: 'newest' | 'oldest') => {
    setSort(s);
    setComments([]);
    setNextKey(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    setError('');
    try {
      const { comment } = await api.postComment(dashboardId, text.trim());
      setText('');
      // Prepend or append depending on sort order
      setComments((prev) => sort === 'newest' ? [comment, ...prev] : [...prev, comment]);
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="comments-section">
      <div className="comments-header">
        <h3 className="comments-title">Comments</h3>
        <div className="comments-sort">
          <button type="button" className={`btn ghost comments-sort-btn${sort === 'newest' ? ' active' : ''}`} onClick={() => changeSort('newest')}>Newest</button>
          <button type="button" className={`btn ghost comments-sort-btn${sort === 'oldest' ? ' active' : ''}`} onClick={() => changeSort('oldest')}>Oldest</button>
        </div>
      </div>

      {user && (
        <form className="comment-form" onSubmit={submit}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment…"
            rows={3}
            maxLength={2000}
            disabled={posting}
          />
          {error && <p className="error">{error}</p>}
          <button type="submit" className="btn primary" disabled={posting || !text.trim()}>
            {posting ? 'Posting…' : 'Post'}
          </button>
        </form>
      )}

      {loading && comments.length === 0
        ? <p className="muted">Loading…</p>
        : comments.length === 0
          ? <p className="muted">No comments yet.</p>
          : (
            <ul className="comment-list">
              {comments.map((c) => (
                <li key={c.commentId} className="comment-item">
                  <div className="comment-meta">
                    <span className="comment-author">{c.authorName}</span>
                    <span className="comment-date">{new Date(c.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <p className="comment-body">{c.text}</p>
                </li>
              ))}
            </ul>
          )
      }

      {nextKey && (
        <button type="button" className="btn" onClick={() => load(sort, nextKey)} disabled={loading}>
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </section>
  );
}

const iconProps = {
  width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
} as const;

const IconExternal = () => (
  <svg {...iconProps}>
    <path d="M15 3h6v6" /><path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
  </svg>
);
const IconDownload = () => (
  <svg {...iconProps}>
    <path d="M12 3v12" /><path d="m7 11 5 5 5-5" /><path d="M5 21h14" />
  </svg>
);
const IconPencil = () => (
  <svg {...iconProps}>
    <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
const IconKebab = () => (
  <svg {...iconProps} fill="currentColor" stroke="none">
    <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
  </svg>
);
const IconShare = () => (
  <svg {...iconProps}>
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <path d="m8.6 13.5 6.8 4" /><path d="m15.4 6.5-6.8 4" />
  </svg>
);
const IconUpload = () => (
  <svg {...iconProps}>
    <path d="M12 15V4" /><path d="m7 8 5-5 5 5" /><path d="M5 21h14" />
  </svg>
);
const IconCheck = () => (
  <svg {...iconProps}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Loads the embedded page via fetch+srcdoc (instead of src) so its origin matches ours
// (sandbox="... allow-same-origin" + srcdoc inherits the parent's origin), which lets us
// read its real rendered size and scale it to fit — fixing cases where a fixed-resolution
// upload (e.g. a game export) would otherwise get clipped by the iframe's box. The container
// keeps the frame's normal fixed box size; the content is stretched (independently on each
// axis) to fill it, rather than the box resizing itself around the content.
//
// The iframe is always mounted the same way regardless of the scaleToFit toggle — only its
// transform/size style changes — so toggling never re-navigates the iframe (which would wipe
// any in-progress state, e.g. a game's in-memory progress) the way swapping between two
// different elements (or changing src/srcDoc) would.
function DashboardCanvasFrame({ viewUrl, title, sandbox, scaleToFit }: { viewUrl: string; title: string; sandbox: string; scaleToFit: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [srcDoc, setSrcDoc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [nativeSize, setNativeSize] = useState<{ width: number; height: number } | null>(null);
  // Decided once per load, from whether the content overflows the box it's given at its
  // default 100%-fill size — not re-evaluated afterward, since locking the iframe to a fixed
  // native size (once detected) removes the overflow, which would otherwise flip this back
  // and forth forever.
  const isFixedSizeRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setSrcDoc(null);
    setFailed(false);
    setNativeSize(null);
    isFixedSizeRef.current = false;
    fetch(viewUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        return res.text();
      })
      .then((html) => {
        if (cancelled) return;
        // Relative asset paths in the uploaded page need to resolve against its own
        // folder, not about:srcdoc — a <base> tag fixes that up. Use the FULL url (not just
        // the folder) so document.baseURI inside the iframe exactly mirrors what
        // location.href would be on a normal top-level load, including the signed
        // exp/sig query string — some generated dashboards derive sibling URLs (e.g. a
        // data sidecar file) from their own location, and srcdoc always makes
        // location.href read as "about:srcdoc" regardless of the <base> tag, so anything
        // relying on location.href needs document.baseURI to carry the real URL instead.
        const withBase = /<head[^>]*>/i.test(html)
          ? html.replace(/<head[^>]*>/i, (m) => `${m}<base href="${viewUrl}">`)
          : `<base href="${viewUrl}">${html}`;
        setSrcDoc(withBase);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [viewUrl]);

  // Only commit size changes that are real (>=1px) — the observers fire on the
  // very layout changes we cause, and unguarded setState here produced a
  // feedback loop of re-renders that showed up as flicker.
  const commitContainerSize = (width: number, height: number) =>
    setContainerSize((prev) => {
      const w = Math.round(width);
      const h = Math.round(height);
      return prev.width === w && prev.height === h ? prev : { width: w, height: h };
    });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    commitContainerSize(container.clientWidth, container.clientHeight);
    const observer = new ResizeObserver(([entry]) =>
      commitContainerSize(entry.contentRect.width, entry.contentRect.height)
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    const root = iframeRef.current?.contentDocument?.documentElement;
    if (!root) return;
    const { scrollWidth, scrollHeight, clientWidth, clientHeight } = root;
    // At this point the iframe is still at its default 100%-fill size (nativeSize is null on
    // first load), so clientWidth/Height reflect the box it was actually given. Only classify
    // this as fixed-resolution content if it truly overflows that box — a responsive page
    // (using %/vh units) will match it exactly and should keep filling normally.
    const overflows = scrollWidth > clientWidth + 2 || scrollHeight > clientHeight + 2;
    isFixedSizeRef.current = overflows;
    setNativeSize(overflows ? { width: scrollWidth, height: scrollHeight } : null);
  };

  useEffect(() => {
    if (!isFixedSizeRef.current) return;
    const root = iframeRef.current?.contentDocument?.documentElement;
    if (!root) return;
    // Some game engines resize their canvas after their own load handlers run,
    // so watch for further layout changes rather than measuring only once.
    // Guarded: only commit genuinely different sizes, or every internal
    // relayout of the embedded page triggers a re-render (visible flicker).
    const observer = new ResizeObserver(() => {
      const w = root.scrollWidth;
      const h = root.scrollHeight;
      setNativeSize((prev) => (prev && prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    });
    observer.observe(root);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcDoc, nativeSize !== null]);

  if (failed) {
    return <iframe title={title} src={viewUrl} sandbox={sandbox} className="dashboard-frame" />;
  }

  // Fit by height so the top/bottom edges always align with the container's — the width
  // scales proportionally (preserving aspect ratio). Only applies to content that actually
  // has a fixed native size (see handleLoad) — otherwise (or when scaleToFit is off) fill
  // the box directly with an identity transform, matching the plain-iframe look.
  const applyScale = scaleToFit && nativeSize != null;
  const scale = applyScale && containerSize.height ? containerSize.height / nativeSize.height : 1;
  const scaledWidth = applyScale ? nativeSize.width * scale : containerSize.width;
  const frameStyle = applyScale
    ? { width: nativeSize.width, height: nativeSize.height, transform: `scale(${scale})`, left: 0 }
    : { width: '100%', height: '100%', transform: 'none', left: 0 };
  // Shrink the frame itself to the content's scaled width, rather than centering it inside
  // a full-width box — that's what removes the empty bars on the sides. Reverts to the
  // normal full width when unchecked (or once the content is measured as responsive).
  // marginInline (not the margin shorthand) so the stylesheet's vertical
  // margins — the gap between toolbar and canvas — survive when scaling.
  const containerStyle = applyScale ? { width: scaledWidth, marginInline: 'auto' } : undefined;

  return (
    <div ref={containerRef} className="dashboard-frame dashboard-frame-scaled" style={containerStyle}>
      {srcDoc && (
        <iframe
          ref={iframeRef}
          title={title}
          srcDoc={srcDoc}
          sandbox={sandbox}
          onLoad={handleLoad}
          className="dashboard-frame-inner"
          style={frameStyle}
        />
      )}
    </div>
  );
}

export default function DashboardViewPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [viewUrl, setViewUrl] = useState('');
  const [scaleToFit, setScaleToFit] = useState(true);
  const [versions, setVersions] = useState<DashboardVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>();
  const [shares, setShares] = useState<ShareUser[]>([]);
  const [signedUrls, setSignedUrls] = useState<SignedUrlRecord[]>([]);
  const [shareEmail, setShareEmail] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [signedUrlTtl, setSignedUrlTtl] = useState<number>(SIGNED_URL_TTL_OPTIONS[0].seconds);
  const [copiedUrl, setCopiedUrl] = useState('');
  const [error, setError] = useState('');
  const [screenshotReplacing, setScreenshotReplacing] = useState(false);
  const [uploadingNewVersion, setUploadingNewVersion] = useState(false);
  const [newVersionError, setNewVersionError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const newVersionInputRef = useRef<HTMLInputElement>(null);
  const screenshotUploadRef = useRef<HTMLInputElement>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  // Kebab menu + Edit Metadata modal (name, tags, screenshot in one place)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaTags, setMetaTags] = useState<string[]>([]);
  const [metaVisibility, setMetaVisibility] = useState<'private' | 'discoverable'>('private');
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaSaved, setMetaSaved] = useState(false);
  const [metaError, setMetaError] = useState('');

  const openFullPage = () => {
    if (viewUrl) window.open(viewUrl, '_blank', 'noopener,noreferrer');
  };

  const downloadHtml = async () => {
    if (!viewUrl) return;
    setDownloading(true);
    try {
      const res = await fetch(viewUrl);
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const filename = `${(dashboard?.title || 'canvas').trim().replace(/[\\/:*?"<>|]+/g, '_')}.html`;
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const load = async (version?: number) => {
    if (!id) return;
    const [{ dashboard: d }, { versions: v }, viewRes] = await Promise.all([
      api.getDashboard(id),
      api.getVersions(id),
      api.getViewUrl(id, version),
    ]);
    setDashboard(d);
    setVersions(v);
    setViewUrl(viewRes.url);
    setSelectedVersion(version || d.currentVersion);
    if (d.userId === user?.userId) {
      const [{ shares: s }, { signedUrls: su }] = await Promise.all([
        api.listShares(id),
        api.listSignedUrls(id),
      ]);
      setShares(s);
      setSignedUrls(su);
    }
  };

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [id, user?.userId]);

  useEffect(() => {
    if (dashboard && (dashboard.userId === user?.userId || user?.role === 'admin')) {
      api.listTags().then(({ tags }) => setAvailableTags(tags)).catch(() => {});
    }
  }, [dashboard?.dashboardId, user?.userId, user?.role]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const openMetaModal = () => {
    if (!dashboard) return;
    setMetaTitle(dashboard.title);
    setMetaTags(dashboard.tags ?? []);
    setMetaVisibility(dashboard.visibility === 'discoverable' ? 'discoverable' : 'private');
    setMetaSaved(false);
    setMetaError('');
    setMenuOpen(false);
    setMetaModalOpen(true);
  };

  const toggleMetaTag = (tag: string) => {
    setMetaTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const saveMetadata = async () => {
    if (!id || !dashboard) return;
    setMetaSaving(true);
    setMetaError('');
    try {
      const { dashboard: updated } = await api.updateDashboardMeta(id, {
        title: metaTitle.trim() || dashboard.title,
        tags: metaTags,
        visibility: metaVisibility,
      });
      setDashboard(updated);
      setMetaSaved(true);
    } catch (e: unknown) {
      setMetaError((e as Error).message);
    } finally {
      setMetaSaving(false);
    }
  };

  // After a successful save, show the confirmation briefly, then close.
  useEffect(() => {
    if (!metaSaved) return;
    const t = setTimeout(() => {
      setMetaModalOpen(false);
      setMetaSaved(false);
    }, 1200);
    return () => clearTimeout(t);
  }, [metaSaved]);

  const share = async () => {
    if (!id || !shareEmail) return;
    await api.shareDashboard(id, shareEmail);
    setShareEmail('');
    const { shares: s } = await api.listShares(id);
    setShares(s);
  };

  const createSignedUrl = async () => {
    if (!id) return;
    const created = await api.createSignedUrl(id, { ttlSeconds: signedUrlTtl });
    const { signedUrls: su } = await api.listSignedUrls(id);
    setSignedUrls(su);
    await navigator.clipboard.writeText(created.url);
    setCopiedUrl(created.url);
  };

  const copyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedUrl(url);
  };

  const onVersionChange = async (v: number) => {
    setSelectedVersion(v);
    const viewRes = await api.getViewUrl(id!, v);
    setViewUrl(viewRes.url);
  };

  if (!dashboard) {
    if (error) return <div className="center">{error}</div>;
    return (
      <div className="dashboard-view">
        <div className="page-header">
          <div style={{ flex: 1 }}>
            <Skeleton width="40%" height="1.8rem" style={{ marginBottom: '0.5rem' }} />
            <Skeleton width="60%" height="0.9rem" />
          </div>
          <div className="actions">
            <Skeleton width="72px" height="2rem" radius="6px" />
            <Skeleton width="100px" height="2rem" radius="6px" />
            <Skeleton width="64px" height="2rem" radius="6px" />
          </div>
        </div>
        <Skeleton width="100%" height="70vh" radius="12px" style={{ marginTop: '1rem' }} />
      </div>
    );
  }

  const isOwner = dashboard.userId === user?.userId;
  // Admins can manage any canvas; sharing stays owner-only.
  const canManage = isOwner || user?.role === 'admin';
  const isUploaded = Boolean(dashboard.uploadType) && dashboard.uploadType !== 'generated';

  const replaceScreenshot = async (file: File) => {
    if (!id) return;
    setScreenshotReplacing(true);
    try {
      const { presignedUrl, screenshotKey } = await api.getScreenshotUploadUrl(id, file.type);
      await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const { screenshotUrl } = await api.confirmScreenshot(id, screenshotKey);
      setDashboard((d) => d ? { ...d, screenshotUrl } : d);
    } catch (e: unknown) {
      // Screenshot changes happen inside the Edit-metadata modal, so surface
      // the failure there rather than on the page behind it.
      setMetaError((e as Error).message);
    } finally {
      setScreenshotReplacing(false);
    }
  };

  const uploadNewVersion = async (file: File) => {
    if (!id) return;
    setUploadingNewVersion(true);
    setNewVersionError('');
    try {
      const { presignedUrl } = await api.requestUploadNewVersion(id);
      const putRes = await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'text/html' } });
      if (!putRes.ok) throw new Error('File upload failed');
      const { version } = await api.completeUploadNewVersion(id);
      await load(version);
    } catch (e: unknown) {
      setNewVersionError((e as Error).message);
    } finally {
      setUploadingNewVersion(false);
      if (newVersionInputRef.current) newVersionInputRef.current.value = '';
    }
  };

  const shareModal = shareModalOpen && isOwner && (
    <div className="modal-overlay" onClick={() => setShareModalOpen(false)} role="presentation">
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <h3>Share dashboard</h3>
        <section>
          <h4>Share with colleague</h4>
          <div className="row">
            <input
              type="email"
              placeholder="colleague@lionstudios.cc"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
            />
            <button type="button" className="btn primary" onClick={share}>Share</button>
          </div>
          <ul>
            {shares.map((s) => (
              <li key={s.userId}>
                {s.displayName} ({s.email})
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => api.unshareDashboard(id!, s.userId).then(() => load())}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h4>Create Shareable Link</h4>
          <label>
            Link duration
            <select value={signedUrlTtl} onChange={(e) => setSignedUrlTtl(Number(e.target.value))}>
              {SIGNED_URL_TTL_OPTIONS.map((o) => (
                <option key={o.seconds} value={o.seconds}>{o.label}</option>
              ))}
            </select>
          </label>
          <button type="button" className="btn primary" onClick={createSignedUrl}>Create Shareable Link</button>
          {copiedUrl && <p className="notice">Copied to clipboard!</p>}
        </section>
        {signedUrls.length > 0 && (
          <section>
            <h4>Active signed links</h4>
            <ul className="signed-url-list">
              {signedUrls.map((su) => (
                <li key={su.urlId}>
                  <span className="muted" title={su.url}>{su.url.replace(/^https?:\/\//, '').slice(0, 48)}…</span>
                  <span className="muted">{su.expiresAt ? `expires ${new Date(su.expiresAt).toLocaleDateString()}` : 'never expires'}</span>
                  <button type="button" className="btn ghost" onClick={() => copyUrl(su.url)}>Copy</button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <button type="button" className="btn" onClick={() => setShareModalOpen(false)}>Close</button>
      </div>
    </div>
  );

  const metaModal = metaModalOpen && canManage && (
    <div className="modal-overlay" onClick={() => setMetaModalOpen(false)} role="presentation">
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <h3>Edit metadata</h3>
        <label>
          Name
          <input
            value={metaTitle}
            maxLength={200}
            onChange={(e) => setMetaTitle(e.target.value)}
          />
        </label>
        <label style={{ marginTop: 'var(--space-4)', display: 'block' }}>
          Visibility
          <select
            value={metaVisibility}
            onChange={(e) => setMetaVisibility(e.target.value as 'private' | 'discoverable')}
          >
            <option value="private">Private (only you and people you share with)</option>
            <option value="discoverable">Discoverable (appears in Browse)</option>
          </select>
        </label>
        <div className="field" style={{ marginTop: 'var(--space-4)' }}>
          <span className="field-label">Tags</span>
          {availableTags.length === 0 && <p className="muted">No tags available.</p>}
          <div className="meta-tag-options">
            {availableTags.map((tag) => (
              <label key={tag} className="tag-editor-option">
                <input
                  type="checkbox"
                  checked={metaTags.includes(tag)}
                  onChange={() => toggleMetaTag(tag)}
                />
                <span>{tag}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="field" style={{ marginTop: 'var(--space-4)' }}>
          <span className="field-label">Screenshot</span>
          <div className="screenshot-preview">
            {dashboard.screenshotUrl && (
              <img src={dashboard.screenshotUrl} alt="Current screenshot" className="screenshot-thumb" />
            )}
            <label className="btn" style={{ cursor: 'pointer' }}>
              {screenshotReplacing ? 'Uploading…' : dashboard.screenshotUrl ? 'Replace screenshot' : 'Upload screenshot'}
              <input
                ref={screenshotUploadRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                disabled={screenshotReplacing}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) replaceScreenshot(f); }}
              />
            </label>
          </div>
        </div>
        {metaError && <p className="error" style={{ marginTop: 'var(--space-4)' }}>{metaError}</p>}
        <div className="meta-modal-footer">
          {metaSaved && <span className="meta-saved-msg"><IconCheck /> Changes saved</span>}
          <button type="button" className="btn" onClick={() => setMetaModalOpen(false)} disabled={metaSaving}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={saveMetadata} disabled={metaSaving || metaSaved}>
            {metaSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  const deleteConfirmModal = deleteConfirmOpen && (
    <div className="modal-overlay" onClick={() => setDeleteConfirmOpen(false)} role="presentation">
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <h3>Delete Upload</h3>
        <p>Are you sure you want to delete this upload? This action cannot be undone. The upload will no longer be recoverable, and any shared links will stop working.</p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <button type="button" className="btn" onClick={() => setDeleteConfirmOpen(false)}>Cancel</button>
          <button
            type="button"
            className="btn danger"
            onClick={() => api.deleteDashboard(id!).then(() => { window.location.href = '/'; })}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const frameVisible = Boolean(viewUrl) && (isUploaded || dashboard.status === 'active');

  return (
    <div className="dashboard-view">
      <div className="report-header">
        <div className="report-meta">
          <h1>{dashboard.title}</h1>
          <p className="muted canvas-creator">
            {dashboard.createdByName && (
              <>
                {dashboard.createdByName}
                {' · '}
                {new Date(dashboard.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                {' · '}
              </>
            )}
            {dashboard.visibility === 'discoverable' ? 'Discoverable' : 'Private'}
          </p>
          {(dashboard.tags ?? []).length > 0 && (
            <div className="tag-chips">
              {(dashboard.tags ?? []).map((t) => <span key={t} className="chip">{t}</span>)}
            </div>
          )}
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {/* One toolbar for everything you can do with the canvas: view controls on
          the left, management on the right. */}
      <div className="frame-toolbar">
        <div className="frame-toolbar-group">
          {viewUrl && (
            <button type="button" className="btn ghost" onClick={openFullPage}><IconExternal /> Open full page</button>
          )}
          {versions.length > 1 && (
            <label className="version-select">
              Version
              <select
                value={selectedVersion}
                onChange={(e) => onVersionChange(Number(e.target.value))}
              >
                {versions.map((v) => {
                  const num = parseInt(v.SK.replace('VERSION#', ''), 10);
                  const date = v.createdAt ? new Date(v.createdAt).toLocaleDateString() : '';
                  return <option key={v.SK} value={num}>v{num}{date ? ` - ${date}` : ''}</option>;
                })}
              </select>
            </label>
          )}
          {frameVisible && isUploaded && (
            <label className="scale-to-fit-toggle">
              <input
                type="checkbox"
                checked={scaleToFit}
                onChange={(e) => setScaleToFit(e.target.checked)}
              />
              Scale to fit
            </label>
          )}
        </div>
        {(canManage || (isUploaded && Boolean(viewUrl))) && (
          <div className="frame-toolbar-group">
            {canManage && isUploaded && (
              <input
                ref={newVersionInputRef}
                type="file"
                accept=".html,.htm"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadNewVersion(file);
                }}
              />
            )}
            {isOwner && (
              <button type="button" className="btn ghost" onClick={() => setShareModalOpen(true)}>
                <IconShare /> Share
              </button>
            )}
            <div className="toolbar-menu" ref={menuRef}>
              <button
                type="button"
                className="btn ghost toolbar-menu-toggle"
                onClick={() => setMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="More actions"
              >
                <IconKebab />
              </button>
              {menuOpen && (
                <div className="menu-dropdown" role="menu">
                  {isUploaded && viewUrl && (
                    <button
                      type="button"
                      className="menu-item"
                      role="menuitem"
                      disabled={downloading}
                      onClick={() => { setMenuOpen(false); downloadHtml(); }}
                    >
                      <IconDownload /> {downloading ? 'Downloading…' : 'Download'}
                    </button>
                  )}
                  {canManage && isUploaded && (
                    <button
                      type="button"
                      className="menu-item"
                      role="menuitem"
                      disabled={uploadingNewVersion}
                      onClick={() => { setMenuOpen(false); newVersionInputRef.current?.click(); }}
                    >
                      <IconUpload /> {uploadingNewVersion ? 'Uploading…' : 'Upload new version'}
                    </button>
                  )}
                  {canManage && (
                    <>
                      <button type="button" className="menu-item" role="menuitem" onClick={openMetaModal}>
                        <IconPencil /> Edit metadata
                      </button>
                      <button
                        type="button"
                        className="menu-item menu-item-danger"
                        role="menuitem"
                        onClick={() => { setMenuOpen(false); setDeleteConfirmOpen(true); }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {frameVisible && (
        <DashboardCanvasFrame
          viewUrl={viewUrl}
          title={dashboard.title}
          sandbox={isUploaded ? 'allow-scripts allow-same-origin allow-forms' : 'allow-scripts allow-same-origin'}
          scaleToFit={isUploaded && scaleToFit}
        />
      )}
      {newVersionError && <p className="error">{newVersionError}</p>}

      <StarRating dashboardId={dashboard.dashboardId} initialAverage={dashboard.ratingAverage} initialCount={dashboard.ratingCount} />

      <CommentsSection dashboardId={dashboard.dashboardId} />

      {shareModal}
      {deleteConfirmModal}
      {metaModal}
    </div>
  );
}
